// CircuitSystem — the Cupra FIP circuit calendar. Follows the league season:
// when a season starts, four away events (Bronce → Platino) get scheduled;
// the club enters its best pair, events resolve in one weekly tick (away
// trips, no day-by-day drama), players earn ranking points and the club
// earns prizes. Ranking resets each season into `history`.

import { EventBus, GameEvents } from '../utils/EventBus'
import { useGameStore } from '@/store/gameStore'
import {
  CIRCUIT_TIERS,
  CircuitCalendarSlot,
  CircuitState,
  generateCircuitCalendar,
  getTravelCost,
  pointsForRun,
} from '@/config/circuitConfig'
import { getDivisionOrNone, meetsDivision, divisionGateLabel } from '@/config/federationConfig'
import { getEffectiveRating, isAvailable } from '@/config/playersConfig'
import { PadelTeam, simulateMatch } from './MatchSimulator'
import { generatePairName } from '@/config/playersConfig'
import { Rng, defaultRng, rollInt } from '../utils/rng'

export interface CircuitEntryCheck {
  can: boolean
  reasons: string[]
  travelCost: number
}

/** Can the club enter this calendar slot right now? */
export function canEnterCircuitEvent(slot: CircuitCalendarSlot): CircuitEntryCheck {
  const store = useGameStore.getState()
  const def = CIRCUIT_TIERS[slot.tier]
  const reasons: string[] = []
  const travelCost = getTravelCost(slot, store.region)

  if (slot.result) reasons.push('El evento ya se ha disputado')
  else if (slot.entered) reasons.push('Ya estás inscrito')
  else if (store.week >= slot.week) reasons.push('Inscripción cerrada')

  if (!meetsDivision(getDivisionOrNone(store.league), def.minDivision)) {
    reasons.push(divisionGateLabel(def.minDivision))
  }
  const available = store.players.filter(isAvailable)
  if (available.length < 2) {
    reasons.push('Necesitas al menos 2 jugadores disponibles')
  }
  // Finals: the invitation is earned with season ranking points
  if (def.minPairPoints !== undefined && store.circuit) {
    const ranking = store.circuit.ranking
    const pairPoints = [...available]
      .sort((a, b) => getEffectiveRating(b) - getEffectiveRating(a))
      .slice(0, 2)
      .reduce((sum, p) => sum + (ranking[p.id] ?? 0), 0)
    if (pairPoints < def.minPairPoints) {
      reasons.push(`Requiere ${def.minPairPoints} puntos de ranking de la pareja (llevan ${pairPoints})`)
    }
  }
  if (store.money < travelCost) reasons.push('No puedes pagar el viaje')

  return { can: reasons.length === 0, reasons, travelCost }
}

/** Enter the club's best pair; pays the travel cost up front. */
export function enterCircuitEvent(slotId: string): boolean {
  const store = useGameStore.getState()
  const circuit = store.circuit
  if (!circuit) return false
  const slot = circuit.calendar.find(s => s.id === slotId)
  if (!slot) return false

  const check = canEnterCircuitEvent(slot)
  if (!check.can) return false

  const pair = store.players.filter(isAvailable)
    .sort((a, b) => getEffectiveRating(b) - getEffectiveRating(a))
    .slice(0, 2)
    .map(p => p.id)

  if (check.travelCost > 0) {
    store.subtractMoney(check.travelCost, `Viaje: ${slot.name}`)
  }
  store.setCircuit({
    ...circuit,
    calendar: circuit.calendar.map(s =>
      s.id === slotId ? { ...s, entered: true, pairIds: pair } : s,
    ),
  })
  return true
}

export class CircuitSystem {
  private handler = () => this.processWeek()

  constructor(private rng: Rng = defaultRng) {
    EventBus.on(GameEvents.WEEK_CHANGED, this.handler)
  }

  destroy() {
    EventBus.off(GameEvents.WEEK_CHANGED, this.handler)
  }

  private processWeek() {
    const store = useGameStore.getState()
    const league = store.league
    if (!league) return

    this.syncSeason(league.season, league.seasonStartWeek)
    this.resolveDueEvents()
  }

  /** (Re)generate the calendar when a new league season starts */
  private syncSeason(season: number, seasonStartWeek: number) {
    const store = useGameStore.getState()
    const circuit = store.circuit

    if (circuit?.season === season) return

    // Archive the outgoing season's best pair points
    const history = circuit
      ? [...circuit.history, {
          season: circuit.season,
          points: Object.values(circuit.ranking).reduce((max, p) => Math.max(max, p), 0),
        }]
      : []

    const next: CircuitState = {
      season,
      calendar: generateCircuitCalendar(season, seasonStartWeek, this.rng),
      ranking: {},
      history,
    }
    store.setCircuit(next)
  }

  private resolveDueEvents() {
    const store = useGameStore.getState()
    const circuit = store.circuit
    if (!circuit) return

    let changed = false
    const calendar = circuit.calendar.map(s => ({ ...s }))
    const ranking = { ...circuit.ranking }

    for (const slot of calendar) {
      if (slot.result || store.week < slot.week) continue
      if (!slot.entered || !slot.pairIds) continue // missed events stay empty

      slot.result = this.playEvent(slot, ranking)
      changed = true
    }

    if (changed) {
      store.setCircuit({ ...circuit, calendar, ranking })
    }
  }

  /** Knockout run: the club pair plays round by round until it loses or
   *  lifts the trophy. Resolved in one tick — it's an away trip. */
  private playEvent(slot: CircuitCalendarSlot, ranking: Record<string, number>) {
    const store = useGameStore.getState()
    const def = CIRCUIT_TIERS[slot.tier]
    const totalRounds = Math.round(Math.log2(def.teams))

    const pair = (slot.pairIds ?? [])
      .map(id => store.players.find(p => p.id === id))
      .filter((p): p is NonNullable<typeof p> => Boolean(p))

    // Pair fell apart (players released mid-season): walkover, no points
    if (pair.length < 2) {
      return { roundsWon: 0, totalRounds, champion: false, points: 0, prize: 0 }
    }

    const clubTeam: PadelTeam = {
      name: `${pair[0].name.split(' ').pop()} / ${pair[1].name.split(' ').pop()}`,
      rating: (pair[0].level + pair[1].level) / 2,
      form: (pair[0].form + pair[1].form) / 2,
      isClubTeam: true,
    }

    let roundsWon = 0
    for (let round = 0; round < totalRounds; round++) {
      // Opposition toughens deeper in the draw
      const [min, max] = def.npcRatingRange
      const depth = round / Math.max(1, totalRounds - 1)
      const floor = Math.round(min + (max - min) * depth * 0.6)
      const npc: PadelTeam = {
        name: generatePairName(this.rng),
        rating: rollInt(floor, max, this.rng),
        form: rollInt(35, 65, this.rng),
        isClubTeam: false,
      }
      const result = simulateMatch(clubTeam, npc, { rng: this.rng })
      if (result.winner !== 0) break
      roundsWon++
    }

    const champion = roundsWon === totalRounds
    const points = pointsForRun(def, roundsWon, totalRounds)
    const prize = champion ? def.prizeChampion : 0

    // Points to both players' season ranking; stats, form and fatigue
    for (const player of pair) {
      ranking[player.id] = (ranking[player.id] ?? 0) + points
      store.updatePlayer(player.id, {
        matchesPlayed: player.matchesPlayed + roundsWon + (champion ? 0 : 1),
        matchesWon: player.matchesWon + roundsWon,
        form: Math.max(0, Math.min(100, player.form + (champion ? 8 : roundsWon > 0 ? 2 : -3))),
        fatigue: Math.min(100, (player.fatigue ?? 0) + 12),
      })
    }

    if (champion) {
      store.addMoney(prize, `Campeones del ${slot.name}`)
      store.addReputation(def.scope === 'nacional' ? 0.15 : 0.08)
    }

    EventBus.emit(GameEvents.CIRCUIT_EVENT_RESOLVED, {
      name: slot.name,
      tier: def.name,
      champion,
      points,
      prize,
    })
    EventBus.emit(GameEvents.NOTIFY, {
      message: champion
        ? `🏆 ¡Campeones del ${slot.name}! +${points} puntos y ${prize.toLocaleString('es-ES')}€`
        : `${slot.name}: eliminados tras ${roundsWon} ronda${roundsWon === 1 ? '' : 's'} · +${points} puntos`,
      type: champion ? 'success' : 'info',
    })

    return { roundsWon, totalRounds, champion, points, prize }
  }
}
